import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

export default function RequestPortalDialog({ open, onOpenChange, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      portal_url: "",
      username: "",
      password: "",
      description: "",
    },
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await api.post("/catalog/request-portal", {
        portal_url: data.portal_url,
        username: data.username,
        password: data.password,
        description: data.description,
      });
      toast.success("Portal request submitted. We will notify you when it's ready.");
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to submit request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (isOpen) => {
    if (!isOpen) {
      reset();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Custom Portal</DialogTitle>
          <DialogDescription>
            Submit a request for a portal not in our catalog. We'll build a
            connection for it and add it to your subscriptions.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="portal_url">Portal URL</Label>
            <Input
              id="portal_url"
              type="url"
              placeholder="https://portal.example.com"
              {...register("portal_url", {
                required: "Portal URL is required",
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: "Enter a valid URL starting with http:// or https://",
                },
              })}
            />
            {errors.portal_url && (
              <p className="text-xs text-red-500">{errors.portal_url.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="req-username">Username (email)</Label>
            <Input
              id="req-username"
              type="email"
              placeholder="your@email.com"
              {...register("username", {
                required: "Username is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Enter a valid email address",
                },
              })}
            />
            {errors.username && (
              <p className="text-xs text-red-500">{errors.username.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="req-password">Password</Label>
            <Input
              id="req-password"
              type="password"
              placeholder="Enter your portal password"
              {...register("password", {
                required: "Password is required",
              })}
            />
            {errors.password && (
              <p className="text-xs text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{ "--tw-ring-color": BRAND.bg }}
              rows={3}
              placeholder="Describe what documents you need from this portal..."
              {...register("description", {
                required: "Description is required",
              })}
            />
            {errors.description && (
              <p className="text-xs text-red-500">{errors.description.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="text-white"
              style={{ backgroundColor: BRAND.bg }}
              onMouseEnter={(e) =>
                !submitting && (e.currentTarget.style.backgroundColor = BRAND.hover)
              }
              onMouseLeave={(e) =>
                !submitting && (e.currentTarget.style.backgroundColor = BRAND.bg)
              }
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
